import { ROUTE_GET_BETTER_DEALS, ROUTE_BILLS_PAGE } from '@1bill-app/constants';
import { GoogleAddressDetails, GoogleAddressService} from '@1bill-app/services/google/google-address.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, IonContent, LoadingController, NavController, ToastController } from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { BillCategoryKey, ViewBillType } from '@1bill-app/services/bill/types';
import { ActiveMeters, BetterDealProductPayload, MsatsDiscoveryResponse } from '@1bill-app/services/better-deal/better-deal.type';
import { States } from '@1bill-app/services/better-deal/better-deal.type';
import { getDayCount } from '@1bill-app/helpers/date.helpers';
import { BetterDealQuery } from '@1bill-app/services/better-deal/better-deal.query';
import { BetterDealService } from '@1bill-app/services/better-deal/better-deal.service';

import { SubSink } from 'subsink';
import { objDeepEquals } from '@1bill-app/helpers/object.helper';

@Component({
  selector: 'app-get-better-deals-filters',
  templateUrl: './get-better-deals-filters.page.html',
  styleUrls: ['./get-better-deals-filters.page.scss'],
})
export class GetBetterDealsFiltersPage implements OnInit {  
  streetTypes$ = this.betterDealQuery.streetTypes$.pipe(map(res => res));
  bill$ = this.betterDealQuery.bill$;  
  
  selectedAddressNmi: string;  
  isProcessingAddress: boolean = true;

  billDetails: ViewBillType;
  activeMeters: ActiveMeters[];
  electricityDistributorId: number;
  gasDistributorId: number;
  subs = new SubSink();

  //Address Form
  streetTypes: string[];
  states: string[] = States;
  customAddress = new FormGroup({
    subpremise: new FormControl(''),
    streetNo: new FormControl('', [Validators.required]),
    streetName: new FormControl('', Validators.required),
    streetType: new FormControl(''),
    suburb: new FormControl('', Validators.required),
    state: new FormControl('', Validators.required),
    postcode: new FormControl('', Validators.required),
  });
  addresses: MsatsDiscoveryResponse[] | any[] = [];
  initialFormValues: any;
  isFormUntouched: boolean;

  //Filters
  fuelType: 'Electricity' | 'Gas' | 'Dual';
  isSolar?: boolean;
  isResidential?:boolean;
  isLifeSupport?:boolean;
  showDealsButton: boolean = false;
  showResidential: boolean = true;

  @ViewChild(IonContent, { static: false }) content: IonContent;

  constructor(   
    private logger: NGXLogger,
    private router: Router,   
    private _gAddSrv: GoogleAddressService,    
    private toastController : ToastController,
    private loadingController: LoadingController,
    private alertCtrl: AlertController,
    private navCtrl: NavController,
    private betterDealQuery : BetterDealQuery,
    private betterDealService: BetterDealService
  ) {}

  ngOnInit() {
    this.subs.sink = this.betterDealService.getStreetTypes().subscribe();    
    this.subs.sink = this.bill$.subscribe((res) => this.billDetails = res);    
    
    if(this.billDetails != null){
      this.fuelType = this.billDetails.categoryKey == BillCategoryKey.ELECTRICITY ? 'Electricity' : 'Gas';
      this.isSolar = this.billDetails.categoryKey == BillCategoryKey.GAS ? false : undefined;
      this.getMsatsDiscoveryResults(this.billDetails.fullAddress);  
    }
    else{
      this.navCtrl.navigateRoot([ROUTE_BILLS_PAGE]);     
    }    
  }

  ngOnDestroy() {    
    this.subs.unsubscribe();
  }

  async getMsatsDiscoveryResults(addressStr: string = null) {   
    this.isProcessingAddress = true;
    const loading = await this.loadingController.create({
      message: 'Parsing bill address...',
    });
    loading.present();
    
    let formattedAddress = addressStr;
    let addressArray = addressStr.split(' ');
    //Remove Lot number from address
    //This confuse google as it takes it as subpremise/unit number
    if(addressArray[0] == 'Lot' || addressArray[0] == 'LOT'){
      addressArray.splice(0, 2);
      formattedAddress =  addressArray.join(' ').trim();          
    }
      of(formattedAddress)
        .pipe(
          mergeMap((address) => {
            return from(this._gAddSrv.getPlacePredictionsFromGeocode(address)).pipe(
              map((res) => res),
            );
          }),
          tap((res) => {
            if(res && res.result){
              this.initialFormValues = {
                subpremise: res.result.subpremise?.replace(/\D/g, '') ?? '',
                streetNo: res.result.streetNo ?? '',
                streetName: res.result.streetName ?? '',
                streetType: res.result.streetType ?? '',
                suburb: res.result.city ?? '',
                state: res.result.state ?? '',
                postcode: res.result.postcode,
              };
              this.customAddress.setValue(this.initialFormValues);
              this.customAddress.markAllAsTouched();            
            }
          }),       
          filter((res) => {
            if (res && res.result && res.result.streetNo) {              
              //get msats results 
              return true;
            } else {
              //Google cant find address
              this.isProcessingAddress = false;
              loading.dismiss();
              this.presentToast('warning', 'Cant read bill address.');
              this.selectedAddressNmi = 'manualAddress'; 
              return false;
            }
          }),          
          filter(()=> {
            if(this.billDetails.categoryKey == BillCategoryKey.ELECTRICITY)
              return true;
            else{
              //If not electiricity, dont call msats
              this.selectedAddressNmi = 'manualAddress';
              this.isProcessingAddress = false;
              loading.dismiss();
              return false;
            }
          }),          
          switchMap((res) => {
            res.result.subpremise = res.result.subpremise?.replace(/\D/g, '') ?? '';
            return this.betterDealService.getMsatsDiscoveryResults(res.result);
          }),
          map((res) => {
            this.addresses = res;
            return res;
          }),          
          tap(() => {
            let addressCount = this.addresses.length;
            if (addressCount == 0) {
              //Msats cant find any result, put address form
              this.selectedAddressNmi = 'manualAddress';
            }            
             else {
              //Many results, let user to select
              this.selectedAddressNmi = this.addresses[0]['nmi'];
              if(this.addresses[0].classificationCode && this.addresses[0].classificationCode == 'RESIDENTIAL'){
                this.isResidential = true;
                this.showResidential = false;
              } 
            }
            loading.dismiss();
            this.isProcessingAddress = false;
          }),
          catchError((err) => {
            this.selectedAddressNmi = 'manualAddress';
            this.isProcessingAddress = false;
            loading.dismiss();
            this.logger.error('Error in createaddressJson', err);            
            return of(false);
          }),
        )
        .subscribe();    
  }

  async searchDeals() {    
    let productPayload: BetterDealProductPayload;
    let partialProductPayloadElectricity:any;
    let partialProductPayloadGas: any;
    let partialProductPayloadAddress : any;
    
    //Call msasts again here if form address changed && electricity
    if(this.selectedAddressNmi == 'manualAddress' && this.billDetails.categoryKey == BillCategoryKey.ELECTRICITY){
      if (!objDeepEquals(this.initialFormValues, this.customAddress.value)) {
        //form is changed
        //Create new payload for msats search
        //Save latest form        
        let newAddress : GoogleAddressDetails = {
          streetNo: this.customAddress.get('streetNo').value,
          streetName: this.customAddress.get('streetName').value,
          streetType: this.customAddress.get('streetType').value,
          subpremise: this.customAddress.get('subpremise').value,
          postcode: this.customAddress.get('postcode').value.toString(),
          city: this.customAddress.get('suburb').value,
          state : this.customAddress.get('state').value,
          country: 'AU',
          placeId: '',
        }
        
        this.initialFormValues = {
          streetNo: this.customAddress.get('streetNo').value,
          streetName: this.customAddress.get('streetName').value,
          streetType: this.customAddress.get('streetType').value,
          subpremise: this.customAddress.get('subpremise').value,
          postcode: this.customAddress.get('postcode').value,
          suburb: this.customAddress.get('suburb').value,
          state : this.customAddress.get('state').value,  
        }
        
        try {
          await this.betterDealService
          .getMsatsDiscoveryResults(newAddress)
          .toPromise()
          .then((res) => {
            this.addresses = res;
            if(this.addresses.length > 0 ){              
              return;
            }
          });        
        } catch (err) {
          this.logger.error('err in msatsResults' , err);        
        }
        //This is second call after form is changed
        //We look up msats results if form changed again
        //If there are results it will return and show options else continue..
        if(this.addresses.length > 0){
          this.selectedAddressNmi = this.addresses[0]['nmi'];
          this.content.scrollToTop(1500);
          return;
        }   
      }      
    }

    //Build Payload
    //This partialProductPayload is the only diff if we got msatdiscovery result   
    //If there is no results, get values from address form
    if (this.selectedAddressNmi == 'manualAddress' || this.addresses.length == 0) {
      partialProductPayloadAddress= {
        streetNo: this.customAddress.get('streetNo').value,
        streetName: this.customAddress.get('streetName').value,
        streetType: this.customAddress.get('streetType').value,
        subpremise: this.customAddress.get('subpremise').value,
        postcode: this.customAddress.get('postcode').value.toString(),
        suburb: this.customAddress.get('suburb').value,
        state : this.customAddress.get('state').value,
      }
    } 
    else{
      //there is msats results, set values from result
      let result = this.addresses.find((x) => x.nmi == this.selectedAddressNmi);
      partialProductPayloadAddress = {
        streetNo: result.address.houseNumber,
        streetName: result.address.street,
        streetType: result.address.streetType,
        subpremise: result.address.flatOrUnitNumber,        
        postcode: result.address.postcode,
        suburb: result.address.suburb,
        state: result.address.state,                
      }
    }

    //Keys for gas
    if(this.billDetails.categoryKey == BillCategoryKey.GAS){
      let p = partialProductPayloadAddress.postcode;
      let s = partialProductPayloadAddress.suburb;

      try {
        await this.betterDealService
          .getGasDistributorId(p, s)
          .toPromise()
          .then((res) => {
            this.gasDistributorId = res;
          });        
      } catch (err) {
        this.logger.error('err in getGasDistributorId' , err);        
      }

      //Add mirn aka dpi in payload
      let billDpi = this.billDetails.dpi ?? '';
      //If address change, remove mirn field as it is not valid anymore
      if(!objDeepEquals(this.initialFormValues, this.customAddress.value)) billDpi = '';

      partialProductPayloadGas = {
        gasBillAvailable: this.billDetails.categoryKey == BillCategoryKey.GAS,
        gasBillDays: this.billDetails.categoryKey == BillCategoryKey.GAS ? (this.billDetails.billDays ?? getDayCount(this.billDetails.billStartDate, this.billDetails.billEndDate) + 1) : 30,
        gasPeakUsage: +this.billDetails.peakUsage ?? 0,
        gasOffPeakUsage: this.billDetails.offPeakUsage ?? 0,
        gasBillType: 'Winter', //Winter or Summer FIXME
        gasReferenceUsage: this.billDetails.categoryKey != BillCategoryKey.GAS,
        gasMeterType: 'Single', //constant
        gasDistributorId: this.gasDistributorId,
        gasCurrentSpendInclGst: +this.billDetails.amount,
        mirn: billDpi
      }       
    }

    //Keys for Electricity
    if(this.billDetails.categoryKey == BillCategoryKey.ELECTRICITY){

      partialProductPayloadElectricity = {
        electricityBillAvailable: this.billDetails.categoryKey == BillCategoryKey.ELECTRICITY,
        electricityBillDays: this.billDetails.categoryKey == BillCategoryKey.ELECTRICITY ?  (this.billDetails.billDays ?? getDayCount(this.billDetails.billStartDate, this.billDetails.billEndDate) + 1) : 30,
        electricityPeakUsage: +this.billDetails.peakUsage ?? 0,
        electricityOffPeakUsage : +this.billDetails.offPeakUsage ?? 0,
        electricityControlledLoadUsage1: +this.billDetails.controlledLoad1 ?? 0,
        electricityControlledLoadUsage2: +this.billDetails.controlledLoad2 ?? 0,
        electricityShoulderUsage: +this.billDetails.shoulder1 ?? 0,
        electricityMeterType: 'Single', // constant
        electricityCurrentSpendInclGst: +this.billDetails.amount,
        electricityReferenceUsage: this.billDetails.categoryKey != BillCategoryKey.ELECTRICITY, //make it false since there is bill..

      }

      if(this.selectedAddressNmi != 'manualAddress'){
        let result = this.addresses.find((x) => x.nmi == this.selectedAddressNmi);
        let distributorId = this.betterDealService.getElectricityDistributorId(result.nmi, result.address.state);
        if(!distributorId){
          let p = partialProductPayloadAddress.postcode;
          let s = partialProductPayloadAddress.suburb;
          try {
            await this.betterDealService
              .getElectricityDistributorIdFromApi(p, s)
              .toPromise()
              .then((res) => {
                distributorId = res;
              });        
          } catch (err) {
            this.logger.error('err in getElectricityDistributorIdFromApi' , err);        
          }
        }
        //Get active meters for selected nmi if there is address
        //This will save nmi and activemeters in state
        try {
          await this.betterDealService
          .getActiveMeters(result.nmi, result.checksum)
          .toPromise()
          .then((res) => {
            this.activeMeters = res['activeMeters'];
          });        
        } catch (err) {
          this.logger.error('err in getActiveMeters' , err);        
        }

        partialProductPayloadElectricity = {
          ...partialProductPayloadElectricity,
          electricityDistributorId: distributorId,
          activeMeters: this.activeMeters,
          nmi: this.selectedAddressNmi
        }
      }
      else{
        let p = partialProductPayloadAddress.postcode;
        let s = partialProductPayloadAddress.suburb;

        try {
          await this.betterDealService
            .getElectricityDistributorIdFromApi(p, s)
            .toPromise()
            .then((res) => {
              this.electricityDistributorId = res;
            });        
        } catch (err) {
          this.logger.error('err in getElectricityDistributorIdFromApi' , err);        
        }

        partialProductPayloadElectricity = {
          ...partialProductPayloadElectricity,
          electricityDistributorId: this.electricityDistributorId,          
        }
      }  
    }

    productPayload = {
      isConnection: false,
      accountType: this.isResidential ? 'Residential' : 'SME',        
      solarPanels:  this.isSolar, //FOR GAS default false
      lifeSupport: this.isLifeSupport,
      fuelType: this.billDetails.categoryKey == BillCategoryKey.ELECTRICITY ? 'Electricity' : 'Gas',
      ...partialProductPayloadAddress,
      ...partialProductPayloadElectricity,
      ...partialProductPayloadGas,
    }

    this.betterDealService.saveFiltersAndAddress(productPayload);    
    this.router.navigate([ROUTE_GET_BETTER_DEALS + '/products']);
  }

  async errorAlert() {
    const alert = await this.alertCtrl.create({
      mode: 'ios',
      cssClass: 'bill-alert alert-icon',
      message:
        '<img src="assets/icon/alert/warning.svg" alt="warning-icon" /><h2 class="alert-title">Oops!</h2><p class="alert-text">Something went wrong! Please try again later.</p>',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Got it',
          handler: () => {
            this.navCtrl.navigateRoot([ROUTE_BILLS_PAGE]);
          },
        },
      ],
    });
    await alert.present();
  }

  async presentToast(color: 'success' | 'danger' | 'warning', message: string) {
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      color,
      buttons: [
        {
          icon: 'close-circle-outline',
          role: 'cancel',
        },
      ],
    });
    toast.present();   
  }

  //If Msats return as residental, dont show res/sme option
  toggleResidential(){
    if(this.selectedAddressNmi == 'manualAddress'){
      this.showResidential = true;
    }
    else{
      let result = this.addresses.find((x) => x.nmi == this.selectedAddressNmi);
      if(result.classificationCode == 'RESIDENTIAL'){
        this.showResidential = false;
        this.isResidential = true;
      }
      else{
        this.showResidential = true;
      }
    }
  }
}